import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { SortUtils } from '../../utils/sort-utils';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { ServerErrorUtils } from '../../utils/server-error-utils';
import { Apps, AppsTableStatusColorMap } from '../../domain/apps.model';
import { AppsService } from '../../services/apps.service';
import { getAppDisplayLabel } from 'src/app/modules/supply-bundles/supply-bundle-form/fields/supply-bundle-publisher-apps.utils';

@Component({
  selector: 'ctv-app-autocomplete',
  template: ` <mat-form-field
    [floatLabel]="floatLabel || 'always'"
    class="w-100"
    [ngClass]="formFieldClass"
    [appearance]="formFieldAppearance"
  >
    <mat-label>{{label ? label : 'Select app'}}</mat-label>
    <input
      matInput
      #search
      aria-label="App"
      [matAutocomplete]="auto"
      [formControl]="filterCtrl"
      [placeholder]="placeholder ? placeholder : 'Search'"
      [required]="required"
    />
    <i class="far fa-spin fa-spinner text-primary" matSuffix [hidden]="!loading"></i>
    <mat-autocomplete
      #auto="matAutocomplete"
      (optionSelected)="optionSelected($event)"
      [displayWith]="display"
    >
      <mat-option *ngFor="let item of filteredItems | async" [value]="item">
      <i class="fas fa-circle status-circle small" ngClass="text-{{AppsTableStatusColorMap[item.status]}}"></i>
        <span>{{ getAppLabel(item) }}</span>
      </mat-option>
    </mat-autocomplete>
  </mat-form-field>`,
})
export class AppsAutocompleteComponent implements OnInit, OnDestroy {
  @ViewChild('search', { static: true }) searchFilter: ElementRef;
  private readonly onDestroy = new Subject<void>();

  @Input() public placeholder: string;
  @Input() public floatLabel: string;
  @Input() public label: string;
  @Input() public formFieldClass: string;
  @Input() public formFieldAppearance: string;
  @Input() required: boolean;
  @Input() preselectedAppId: Apps['id'];
  @Output() appSelected = new EventEmitter<Apps>();

  filteredItems: Subject<Apps[]>; //List of apps to show in dropdown filtered with 'filteredAppIds'
  filterCtrl = new UntypedFormControl();
  appList: Apps[] = []; //stores currently fetched list of apps
  selectedApp: Apps; //currently selected app
  filteredAppIds: string[] = []; //It stores all the Id's of selected apps
  public loading = false;
  public readonly AppsTableStatusColorMap = AppsTableStatusColorMap;
  public readonly getAppLabel = getAppDisplayLabel;

  constructor(
    private service: AppsService,
    private snackBarService: SnackBarService) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(
        (searchTerm) => {
          if (searchTerm && typeof searchTerm === 'string') {
            this.getApps(searchTerm);
          }
          else if (searchTerm == '') { //triggers when user deletes everything from input
            this.filteredItems.next();
            this.appSelected.emit(null);
          }
        }
      );
  }

  ngOnInit(): void {
    if (this.preselectedAppId) {
      this.getApps('', this.preselectedAppId);
    }
  }

  /** SearchTerm used for filtering by pub name. PreselectedAppId used to get only one app (preselected) at the component initialization. */
  private getApps(searchTerm?: string, preselectedAppId?: string) {
    this.loading = true;
    this.filterCtrl.disable();
    return this.service.getApps({
      page: 0,
      size: 1000,
      searchTerm,
      ids: [preselectedAppId]
    })
      .pipe(
        finalize(() => {
          this.filterCtrl.enable();
          !preselectedAppId ? this.searchFilter.nativeElement.focus() : 0; //No need for focus when loading page with preselected app
          this.loading = false;
        }),
        takeUntil(this.onDestroy)
      )
      .subscribe(
        (resp) => {
          this.appList = resp.content.sort(SortUtils.propertyComparatorString('name'));
          if (preselectedAppId) {
            this.selectedApp = this.appList[0]; //Naturally since the api call is filtered with one ID, selectedApp will actually be the first one from the response
            this.filterCtrl.setValue(this.selectedApp);
            this.updateFilteredApps([this.selectedApp]);
            this.emit();
          }
          this.filteredItems.next(this.appList.filter(x => !this.filteredAppIds.includes(x.id)));
        },
        (error) => {
          const messages = ServerErrorUtils.getValidationMessages(error);
          if (messages) {
            messages.forEach((m) => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error while fetching app autocomplete data');
          }
        },
      );
  }

  public optionSelected($event) {
    this.selectedApp = $event.option.value;
    this.emit();
    this.searchFilter.nativeElement.blur(); //blur == unfocus
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty and there is no point in showing filtered list on next focus on element
  }

  /** Emits value back to parent component.
   * If wrapped inside chips autocomplete it emits value to chips component that forwards values to parent component. */
  private emit() {
    this.appSelected.emit(this.selectedApp);
  }

  /** Trigger only when using chips autocomplete. Triggered when item selected through chips autocomplete to update filteredAppIds. */
  public updateFilteredApps(items) {
    this.filteredAppIds = items.map((item) => item.id);
  }

  /** Returns wanted object to input from whole selected item model. */
  public display(app?: Apps) {
    return app ? getAppDisplayLabel(app) || undefined : undefined;
  }

  /** Used only by chips autocomplete component to clear the input from selected item*/
  public resetInput() {
    this.filterCtrl.setValue(null);
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty
  }

  public disable() {
    this.filterCtrl.disable();
  }

  public enable() {
    this.filterCtrl.enable();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }
}
